/* eslint-disable no-console */
import _ from 'lodash';
import { useCallback } from 'react';
import { getErrorMessage } from '../../../utils/errorHandler';
import Logger from '../../../utils/Logger';
import { getVariant } from '../../../utils/variant';
import { getTenantValue } from './constants';

export const useBuyTogetherHandler = ({ product, buytogether, appCtx, cart }) => {
  const slotsConfig = _.get(appCtx, 'siteConfig.textDictionary.PDP_SLOTS', 'PDP-HRT-1,PDP-HRT-2,PDP-BT-1');
  const slotsMap = slotsConfig.split(',').map((s) => s);
  const buyTogetherSlot = _.get(slotsMap, '[2]', '');
  const widgetName = buytogether?.widgetName?.split(' ').join('_') || 'Buy_Together';
  const storeName = Array.isArray(appCtx?.subdomain) ? appCtx.subdomain[0] : appCtx?.subdomain;

  const handleAddToCart = useCallback(() => {
    if (!product || !buytogether?.data?.length) return;

    appCtx.setBusy(true);
    const currentVariantId = _.get(product, 'currentVariant');
    const variants = _.get(product, 'variants');
    const currentVariant = getVariant(variants, currentVariantId);
    const variantOfferingId = _.get(currentVariant, 'offerings[0].offeringId', '');

    const productToAdd = buytogether.data
      .filter((bundle) => bundle.isSelected)
      .map(({ id, offeringId, quantity }) => ({
        skuId: id,
        quantity,
        offeringId,
      }));

    cart
      .addProduct({
        appCtx,
        products: _.unionBy(
          [
            {
              skuId: currentVariantId,
              quantity: 1,
              offeringId: variantOfferingId,
            },
          ],
          productToAdd,
          'skuId'
        ),
        bundledProductOrigin: 'CTA',
        product,
      })
      .then((res) => {
        if (res && res.length) {
          const errorMessage = getErrorMessage(res, appCtx.siteConfig);
          Logger.error(`Failed addToCart for BuyTogether: ${errorMessage}`);
          return;
        }
        const selected = buytogether.data
          .map((item, index) => (item.isSelected ? { id: item.id, index } : null))
          .filter(Boolean);

        const productIds = selected.map((item) => item.id).join('-');
        const productIndexes = selected.map((item) => item.index + 1).join('-');

        const detail = {
          tenant: getTenantValue(appCtx?.store, appCtx?.regionCode),
          store: storeName,
          title: widgetName,
          currentProductID: product.currentVariant,
          productsToAdd: productIds,
          productPositions: productIndexes,
          totalProduct: buytogether.data.length,
          slot: buyTogetherSlot,
        };
        const eventToDigitalData = new CustomEvent('DDPDPBuyTogetherATC', {
          bubbles: true,
          detail,
        });
        window.dispatchEvent(eventToDigitalData);
      })
      .catch((e) => {
        Logger.error(`Failed addToCart for BuyTogether: ${e}`);
      })
      .finally(() => {
        appCtx.setBusy(false);
      });
  }, [product, buytogether, appCtx, cart]);

  const updateAnalyticsClick = (relatedVariants, position) => {
    const detail = {
      tenant: getTenantValue(appCtx?.store, appCtx?.regionCode),
      store: storeName,
      title: widgetName,
      currentProductID: product.currentVariant,
      targetProductID: relatedVariants,
      productPosition: position,
      totalProduct: buytogether.data.length,
      slot: buyTogetherSlot,
    };
    const eventToDigitalData = new CustomEvent('DDPDPBuyTogetherClick', {
      bubbles: true,
      detail,
    });
    window.dispatchEvent(eventToDigitalData);
  };
  const handleImpressionEvent = (relatedProducts = buytogether.data, onLoad = false) => {
    const productsToUse = onLoad ? relatedProducts.slice(0, 3) : relatedProducts;
    const skus = productsToUse.map((p) => p.id).join(',');
    const eventToDigitalData = new CustomEvent('DDPDPBuyTogetherImpressions', {
      bubbles: true,
      detail: {
        payload: { [widgetName]: skus },
      },
    });
    window.dispatchEvent(eventToDigitalData);
  };

  const getImpressionAnalytics = ({ onLoad, relatedProducts }) => {
    handleImpressionEvent(relatedProducts, onLoad);
  };

  const fireImpressionEventOnViewport = () => {
    if (!buytogether?.data?.length) return;
    handleImpressionEvent(buytogether.data, true);
  };

  return {
    handleAddToCart,
    updateAnalyticsClick,
    getImpressionAnalytics,
    fireImpressionEventOnViewport,
  };
};
